import React from 'react';
import { Progress } from 'antd';
import notification from 'utils/notificationBridge';
import { PUBLISH_CONTENT_TYPES } from 'helpers/publishContentTypes';

const PHASE_LABELS = {
  [PUBLISH_CONTENT_TYPES.BANNER]: {
    queued: 'Publishing banner…',
    'uploading-web-image': 'Uploading web image…',
    'uploading-mobile-image': 'Uploading mobile image…',
    creating: 'Saving banner…',
    updating: 'Updating banner…',
  },
  [PUBLISH_CONTENT_TYPES.TEMPLATE]: {
    queued: 'Publishing template…',
    'uploading-fcm-image': 'Uploading FCM image…',
    'uploading-whatsapp-image': 'Uploading WhatsApp image…',
    creating: 'Saving template…',
    updating: 'Updating template…',
  },
  [PUBLISH_CONTENT_TYPES.SHIFT]: {
    queued: 'Publishing shift…',
    creating: 'Saving shift…',
    updating: 'Updating shift…',
  },
  [PUBLISH_CONTENT_TYPES.TRANSACTION]: {
    queued: 'Publishing transaction…',
    creating: 'Saving transaction…',
    updating: 'Updating transaction…',
  },
  [PUBLISH_CONTENT_TYPES.SHIFT_APPLICATION]: {
    queued: 'Publishing shift application…',
    creating: 'Saving shift application…',
    updating: 'Updating shift application…',
  },
  [PUBLISH_CONTENT_TYPES.TIMESHEET]: {
    queued: 'Publishing timesheet…',
    creating: 'Saving timesheet…',
    updating: 'Updating timesheet…',
  },
  [PUBLISH_CONTENT_TYPES.STAFF_DOCUMENTS]: {
    queued: 'Publishing staff documents…',
    creating: 'Saving staff documents…',
    updating: 'Updating staff documents…',
  },
};

const DEFAULT_TITLES = {
  [PUBLISH_CONTENT_TYPES.BANNER]: 'Banner',
  [PUBLISH_CONTENT_TYPES.TEMPLATE]: 'Template',
  [PUBLISH_CONTENT_TYPES.SHIFT]: 'Shift',
  [PUBLISH_CONTENT_TYPES.TRANSACTION]: 'Transaction',
  [PUBLISH_CONTENT_TYPES.SHIFT_APPLICATION]: 'Shift Application',
  [PUBLISH_CONTENT_TYPES.TIMESHEET]: 'Timesheet',
  [PUBLISH_CONTENT_TYPES.STAFF_DOCUMENTS]: 'Staff Documents',
};

const SUCCESS_MESSAGES = {
  [PUBLISH_CONTENT_TYPES.BANNER]: 'Banner added successfully.',
  [PUBLISH_CONTENT_TYPES.TEMPLATE]: 'Template added successfully.',
  [PUBLISH_CONTENT_TYPES.SHIFT]: 'Shift added successfully.',
  [PUBLISH_CONTENT_TYPES.TRANSACTION]: 'Transaction added successfully.',
  [PUBLISH_CONTENT_TYPES.SHIFT_APPLICATION]: 'Shift application added successfully.',
  [PUBLISH_CONTENT_TYPES.TIMESHEET]: 'Timesheet added successfully.',
  [PUBLISH_CONTENT_TYPES.STAFF_DOCUMENTS]: 'Staff documents added successfully.',
};

const SUCCESS_MESSAGES_EDIT = {
  [PUBLISH_CONTENT_TYPES.BANNER]: 'Banner updated successfully.',
  [PUBLISH_CONTENT_TYPES.TEMPLATE]: 'Template updated successfully.',
  [PUBLISH_CONTENT_TYPES.SHIFT]: 'Shift updated successfully.',
  [PUBLISH_CONTENT_TYPES.TRANSACTION]: 'Transaction updated successfully.',
  [PUBLISH_CONTENT_TYPES.SHIFT_APPLICATION]: 'Shift application updated successfully.',
  [PUBLISH_CONTENT_TYPES.TIMESHEET]: 'Timesheet updated successfully.',
  [PUBLISH_CONTENT_TYPES.STAFF_DOCUMENTS]: 'Staff documents updated successfully.',
};

const FAILED_MESSAGES = {
  [PUBLISH_CONTENT_TYPES.BANNER]: 'Failed to publish banner.',
  [PUBLISH_CONTENT_TYPES.TEMPLATE]: 'Failed to publish template.',
  [PUBLISH_CONTENT_TYPES.SHIFT]: 'Failed to publish shift.',
  [PUBLISH_CONTENT_TYPES.TRANSACTION]: 'Failed to publish transaction.',
  [PUBLISH_CONTENT_TYPES.SHIFT_APPLICATION]: 'Failed to publish shift application.',
  [PUBLISH_CONTENT_TYPES.TIMESHEET]: 'Failed to publish timesheet.',
  [PUBLISH_CONTENT_TYPES.STAFF_DOCUMENTS]: 'Failed to publish staff documents.',
};

const FAILED_MESSAGES_EDIT = {
  [PUBLISH_CONTENT_TYPES.BANNER]: 'Failed to update banner.',
  [PUBLISH_CONTENT_TYPES.TEMPLATE]: 'Failed to update template.',
  [PUBLISH_CONTENT_TYPES.SHIFT]: 'Failed to update shift.',
  [PUBLISH_CONTENT_TYPES.TRANSACTION]: 'Failed to update transaction.',
  [PUBLISH_CONTENT_TYPES.SHIFT_APPLICATION]: 'Failed to update shift application.',
  [PUBLISH_CONTENT_TYPES.TIMESHEET]: 'Failed to update timesheet.',
  [PUBLISH_CONTENT_TYPES.STAFF_DOCUMENTS]: 'Failed to update staff documents.',
};

const getPhaseLabel = (contentType, phase) =>
  PHASE_LABELS[contentType]?.[phase] ||
  PHASE_LABELS[contentType]?.queued ||
  'Publishing…';

export const getContentPublishNotificationKey = (jobId) =>
  `content-publish-${jobId}`;

export const openContentPublishNotification = ({
  jobId,
  title,
  contentType,
  phase,
  percent,
}) => {
  notification.open({
    key: getContentPublishNotificationKey(jobId),
    title: title || DEFAULT_TITLES[contentType] || 'Publishing',
    description: (
      <>
        <p style={{ margin: '0 0 8px' }}>{getPhaseLabel(contentType, phase)}</p>
        <Progress percent={percent} size="small" status="active" />
      </>
    ),
    duration: 0,
  });
};

export const closeContentPublishNotification = (jobId) => {
  notification.destroy(getContentPublishNotificationKey(jobId));
};

export const getPublishSuccessMessage = (contentType, mode) =>
  (mode === 'EDIT' ? SUCCESS_MESSAGES_EDIT : SUCCESS_MESSAGES)[contentType] ||
  (mode === 'EDIT' ? 'Updated successfully.' : 'Published successfully.');

export const getPublishFailedMessage = (contentType, mode) =>
  (mode === 'EDIT' ? FAILED_MESSAGES_EDIT : FAILED_MESSAGES)[contentType] ||
  (mode === 'EDIT' ? 'Update failed.' : 'Publish failed.');
